/**
 * Word Labyrinth — choosing the sentence and filling the board (specs/labyrinth-mode.md §6, §7.2).
 *
 * `maze.ts` decides the shape; this file decides what stands on it. Phaser-free, so the whole
 * content contract is testable in node.
 */
import { BUILT_IN_PACKS } from '@/data/packs';
import type { Pack } from '@/data/packs/types';
import { LABYRINTH, pathCellsFor } from './config';
import { buildMaze, routesBetween, type LabyrinthBoard, type Rng } from './maze';
import { readSentenceSets, eligibleSets, type SentenceSet } from './sets';
import { joinablePairs, joinedCells } from './join';

export interface DealtSentence {
  /** The cells of the true path, in reading order. */
  words: string[];
  /** Index after which the full stop / gate post is drawn; `-1` on a single-sentence board. */
  postAfter: number;
  /** Distinct wrong words this board may fill its dead ends with. */
  decoys: string[];
  /** The set ids used, so a run never deals the same sentence twice. */
  setIds: string[];
  /** True when the two halves were grammatically joined rather than merely chained (§6.4). */
  joined: boolean;
}

export interface DealOptions {
  rng?: Rng;
  /** Set ids already used this run. */
  exclude?: Set<string>;
  lang?: string;
}

/**
 * The sentence pack for a language, from the statically imported registry.
 *
 * Resolved by `lang` with an `en` fallback, exactly as `MazeScene` resolves the picture pool — a
 * Satellite earned in the Fables journey still opens a sentence labyrinth, because the treat modes
 * do not belong to whichever lesson happened to pay for them.
 */
export function sentencePackFor(lang: string): Pack | null {
  const match = (l: string): Pack | undefined =>
    BUILT_IN_PACKS.find((p) => p.mode === 'sentence-order' && p.lang === l);
  return match(lang) ?? match(lang.split('-')[0]) ?? match('en') ?? null;
}

/** Every set of a language that a board may use, already filtered by §6.5's length rules. */
export function usableSets(lang: string): SentenceSet[] {
  const pack = sentencePackFor(lang);
  if (!pack) return [];
  return eligibleSets(readSentenceSets(pack), LABYRINTH.maxWordChars, LABYRINTH.minDistinctDecoys);
}

function pick<T>(xs: T[], rng: Rng): T | null {
  return xs.length ? xs[Math.floor(rng() * xs.length)] : null;
}

/**
 * Prefer the longer sentences of whatever fits.
 *
 * Length is the only lever the content gives on how much maze a board gets: the grid is one column
 * narrower than the path (maze.ts), so a 5-tile sentence bends more, fills more of the grid and
 * offers more to read than a 3-tile one. Sampling from the longest half rather than taking the
 * maximum keeps boards from repeating, and the short ones still appear — they are not worse, only
 * smaller, and ja/ko have little else.
 */
function longestHalf(sets: SentenceSet[]): SentenceSet[] {
  if (sets.length < 4) return sets;
  const sorted = sets.slice().sort((a, b) => b.cells.length - a.cells.length);
  return sorted.slice(0, Math.max(2, Math.ceil(sorted.length / 2)));
}

/**
 * Choose the sentence (or pair of sentences) a board will carry.
 *
 * Three grades, in the order the ladder reaches them:
 *  - rounds below `chainFrom`: one sentence, 3–5 cells — the tutorial rung.
 *  - `chainFrom` and up: two complete sentences back to back, separated by a gate post. Works in
 *    all ten locales with no new content, which is why it and not joining carries the long paths.
 *  - `joinFrom` and up: a grammatically joined pair *where the language has one*, falling back to
 *    a plain chain where it does not (ja/ko/th always).
 */
export function dealSentence(
  round: number,
  sets: SentenceSet[],
  opts: DealOptions = {},
): DealtSentence | null {
  const rng = opts.rng ?? Math.random;
  const lang = opts.lang ?? 'en';
  const exclude = opts.exclude ?? new Set<string>();
  const [minCells, maxCells] = pathCellsFor(round);
  const pool = sets.filter((s) => !exclude.has(s.id));
  if (pool.length === 0) return null;

  const fits = (n: number): boolean => n >= minCells && n <= maxCells;
  // Only a COMPLETE sentence may stand on a path alone or be chained to another.
  //
  // A set with no `vorgabe` is a bare noun phrase — `der große Hund`, `el gato negro`. Sentence
  // Order can use one because its board asks for an arrangement, but a labyrinth whose gate opens
  // on `Ich packe meine Tasche ▌ der schnelle Zug` has just told a child that a noun phrase is a
  // sentence, which is the one thing this mode exists to teach otherwise. Fragments are kept for
  // exactly one job: the front half of a grammatical join (§6.4 grade 2), where the predicate that
  // follows turns them into a sentence.
  const complete = pool.filter((s) => s.vorgabe !== '');
  // A decoy that happens to equal a word on the path is not a decoy — it is a second right answer
  // sitting in a dead end. Chaining two sets is what makes this reachable (set B's decoy list knows
  // nothing about set A's tiles), so the filter runs on every board rather than only on chained ones.
  const clean = (decoys: string[], words: string[]): string[] => {
    const onPath = new Set(words);
    return Array.from(new Set(decoys)).filter((d) => !onPath.has(d));
  };

  // A join is the prettiest board this content can make — one long grammatical sentence instead of
  // two stapled together — but it is also a SHORT one, 5-7 cells where a chain reaches 10. Taking
  // it whenever it fits made the top of the ladder the smallest maze in the game. So it must also
  // be competitive on length for its rung: at or within one cell of the longest path the band
  // allows. In practice that puts joins at round 2, whose band was widened to admit them.
  if (round >= LABYRINTH.joinFrom) {
    const worthIt = (n: number): boolean => fits(n) && n >= maxCells - 1;
    const pairs = joinablePairs(pool).filter((p) => worthIt(p.np.tiles.length + p.vp.tiles.length));
    const pair = pick(pairs, rng);
    if (pair) {
      const words = joinedCells(pair, lang);
      const decoys = clean([...pair.np.decoys, ...pair.vp.decoys], words);
      if (decoys.length >= LABYRINTH.minDistinctDecoys) {
        return { words, postAfter: -1, decoys, setIds: [pair.np.id, pair.vp.id], joined: true };
      }
    }
  }

  if (round >= LABYRINTH.chainFrom) {
    // Both halves from the longer end of the pool, for the same reason singles are (see
    // longestHalf): the grid is sized from the path, so a longer chain is a bigger maze with more
    // bends in it. Left to chance the chains came out 6-10 cells at random and rounds 4 and 5
    // plateaued at the same turn count as round 3.
    const long = longestHalf(complete);
    for (let attempt = 0; attempt < LABYRINTH.dealAttempts; attempt++) {
      const a = pick(long, rng);
      const b = pick(long.filter((s) => s.id !== a?.id), rng);
      if (!a || !b) break;
      const words = [...a.cells, ...b.cells];
      if (!fits(words.length)) continue;
      const decoys = clean([...a.decoys, ...b.decoys], words);
      if (decoys.length < LABYRINTH.minDistinctDecoys) continue;
      return { words, postAfter: a.cells.length - 1, decoys, setIds: [a.id, b.id], joined: false };
    }
    return null;
  }

  const single = longestHalf(complete.filter((s) => fits(s.cells.length)));
  const set = pick(single, rng);
  if (!set) return null;
  const decoys = clean(set.decoys, set.cells);
  if (decoys.length < LABYRINTH.minDistinctDecoys) return null;
  return { words: set.cells.slice(), postAfter: -1, decoys, setIds: [set.id], joined: false };
}

/**
 * Fill every cell: the sentence along the true path, decoys everywhere else.
 *
 * Decoys **repeat**, several times each, and that is the intended look rather than a shortfall
 * being papered over (§6.3). A paper letter labyrinth fills its blind corridors with the same six
 * letters over and over, and the repetition is *why* a wrong corridor reads as noise at a glance. A
 * dead end whose every cell is a fresh plausible word would be a second puzzle, not a wall.
 */
function fillGrid(
  shape: Omit<LabyrinthBoard, 'grid'>,
  decoys: string[],
  rng: Rng,
): string[][] {
  const grid: string[][] = Array.from({ length: shape.rows }, () =>
    Array.from({ length: shape.cols }, () => ''));
  shape.path.forEach(([c, r], i) => { grid[r][c] = shape.sentence[i]; });

  // Deal decoys round-robin from a reshuffled bag rather than sampling independently, so no single
  // wrong word happens to cover half the board and the near-misses stay spread across the forks.
  let bag: string[] = [];
  for (let r = 0; r < shape.rows; r++) {
    for (let c = 0; c < shape.cols; c++) {
      if (grid[r][c] !== '') continue;
      if (bag.length === 0) {
        bag = decoys.slice();
        for (let i = bag.length - 1; i > 0; i--) {
          const j = Math.floor(rng() * (i + 1));
          [bag[i], bag[j]] = [bag[j], bag[i]];
        }
      }
      grid[r][c] = bag.pop() as string;
    }
  }
  return grid;
}

/**
 * Deal one complete board.
 *
 * The last gate is §7.2's: **no route other than the true path may spell the target sentence**, and
 * none may spell any other set's sentence either. It almost never fires, because §6.2's decoys are
 * unusable in every slot of their sentence and so cannot continue one at any fork — it is here for
 * the case that rule does not cover, two *chained* sentences whose halves can be re-crossed in the
 * middle to make a third reading.
 */
export function dealBoard(
  round: number,
  sets: SentenceSet[],
  opts: DealOptions = {},
): { board: LabyrinthBoard; sentence: DealtSentence } | null {
  const rng = opts.rng ?? Math.random;
  const known = knownReadings(sets);

  for (let attempt = 0; attempt < LABYRINTH.dealAttempts; attempt++) {
    const sentence = dealSentence(round, sets, opts);
    if (!sentence) continue;
    const shape = buildMaze(round, sentence.words, sentence.postAfter, rng);
    if (!shape) continue;
    const grid = fillGrid(shape, sentence.decoys, rng);
    const board: LabyrinthBoard = { ...shape, grid };
    if (spellsSomethingElse(board, known)) continue;
    return { board, sentence };
  }
  return null;
}

/**
 * Every sentence the pool can spell, keyed the way `spellsSomethingElse` reads a route.
 *
 * One function rather than a `join` at each end, because the two ends silently disagreed once: this
 * set was built with a `\0` separator while the scan read routes with a space, so `known.has(read)`
 * could never be true and §7.2's "no OTHER sentence either" half did nothing for the mode's whole
 * life. What it let through is a chained board whose second sentence is also walkable on its own —
 * the child reads `Ich putze meine Zähne`, which is correct German and the board's own second half,
 * and the gate stays shut with nothing to explain why.
 */
export function knownReadings(sets: SentenceSet[]): Set<string> {
  return new Set(sets.map((s) => s.cells.join(' ')));
}

/** §7.2's check, exported so `deal.test.ts` can assert it directly on a hand-built board. */
export function spellsSomethingElse(board: LabyrinthBoard, known: Set<string>): boolean {
  const target = board.sentence.join(' ');
  const truth = board.path.map(([c, r]) => `${c},${r}`).join('|');
  const routes = routesBetween(
    board.path[0],
    board.path[board.path.length - 1],
    board.cols,
    board.rows,
    board.open,
  );
  for (const route of routes) {
    if (route.map(([c, r]) => `${c},${r}`).join('|') === truth) continue;
    const read = route.map(([c, r]) => board.grid[r][c]).join(' ');
    if (read === target || known.has(read)) return true;
  }
  return false;
}
